import { Box, Typography } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import React, { useRef } from "react";
import theme from "Theme/AppTheme";
import SubdirectoryArrowRightOutlinedIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined";
import { MediaSegmentsType } from "../types";
import useHoverAction from "../hooks/useHoverAction";
import AddButton from "../components/AddButton";
import DeleteButton from "../components/DeleteButton";

type MediaSegmentsProps = {
    mediaSegments?: MediaSegmentsType;
    updateMediaSegments: (key: string, value: string) => void;
    addMediaSegments: () => void;
    removeMediaSegments: () => void;
};

const MediaSegments: React.FC<MediaSegmentsProps> = ({
    mediaSegments,
    updateMediaSegments,
    addMediaSegments,
    removeMediaSegments,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    if (!mediaSegments) {
        return (
            <AddButton onClick={addMediaSegments}>Add Media Segments</AddButton>
        );
    }

    const { rootPath, start, endPrimary, endAlternate, agentName, agentId } =
        mediaSegments;

    return (
        <>
            <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 3 }}>
                Root Path
            </Typography>
            <Box
                sx={{
                    padding: 1,
                    background: theme.palette.white.main,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
                ref={containerRef}
                {...handlers}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AcxMainTextField
                        fullWidth
                        id="media-segment-root-path"
                        value={rootPath}
                        placeholderText="Enter Root Path..."
                        onChange={(e) =>
                            updateMediaSegments("rootPath", e.target.value)
                        }
                        containerStyle={{ flex: 1 }}
                    />
                    <DeleteButton
                        onDelete={removeMediaSegments}
                        showDelete={showAction}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        paddingRight: 3.75,
                    }}
                >
                    <SubdirectoryArrowRightOutlinedIcon
                        sx={{ color: theme.palette.gray[400] }}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
                        Start
                    </Typography>
                    <AcxMainTextField
                        fullWidth
                        containerStyle={{ flex: 16 }}
                        id="media-segment-start"
                        value={start}
                        placeholderText="Enter Value Path..."
                        onChange={(e) =>
                            updateMediaSegments("start", e.target.value)
                        }
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        paddingLeft: 4,
                        paddingRight: 3.75,
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
                        End
                    </Typography>
                    <AcxMainTextField
                        fullWidth
                        id="media-segment-end-primary"
                        value={endPrimary}
                        placeholderText="Enter Primary Value Path..."
                        onChange={(e) =>
                            updateMediaSegments("endPrimary", e.target.value)
                        }
                        containerStyle={{ flex: 8 }}
                    />
                    <AcxMainTextField
                        fullWidth
                        id="media-segment-end-alternate"
                        value={endAlternate}
                        placeholderText="Alternate Value Path..."
                        onChange={(e) =>
                            updateMediaSegments("endAlternate", e.target.value)
                        }
                        containerStyle={{ flex: 8 }}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        paddingLeft: 4,
                        paddingRight: 3.75,
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
                        Agent
                    </Typography>
                    <AcxMainTextField
                        fullWidth
                        id="media-segment-agent-name"
                        value={agentName}
                        placeholderText="Name Value Path..."
                        onChange={(e) =>
                            updateMediaSegments("agentName", e.target.value)
                        }
                        containerStyle={{ flex: 8 }}
                    />
                    <AcxMainTextField
                        fullWidth
                        id="media-segment-agent-id"
                        value={agentId}
                        placeholderText="Id Value Path..."
                        onChange={(e) =>
                            updateMediaSegments("agentId", e.target.value)
                        }
                        containerStyle={{ flex: 8 }}
                    />
                </Box>
            </Box>
        </>
    );
};

export default MediaSegments;
